import { frameAtTime } from '@rcs/physics';
import { ToolButton } from '@rcs/ui';
import { useState, useSyncExternalStore } from 'react';
import { formatGrade, formatSigned, hudValues } from '../hud/values';
import type { SegmentType } from '@rcs/data-schema';
import { SEGMENT_STYLES } from '../course/segment-style';
import { messages } from '../i18n/ja';
import { useMovableWindow } from '../layout/movable';
import type { PlaybackController } from '../playback/controller';
import type { ComputeSuccess } from '../worker/protocol';

/**
 * HUD（FR-ANA-001〜003）。経過時間・走行距離・速度・高度・上下 G を常に表示し、詳細（最高速度・勾配・
 * バンク角・前後・横・合成 G・区間）とデバッグ表示（FPS・物理計算時間）は切り替えで出す。
 *
 * 更新は、再生位置の 0.1 秒刻みの変化（10 Hz）に合わせる。描画ループ（60 Hz）とは独立して、
 * 3D の描画性能に影響しない。
 */
export function Hud({
  ride,
  controller,
  segmentKinds,
  fps,
}: {
  readonly ride: ComputeSuccess | undefined;
  readonly controller: PlaybackController | undefined;
  /** 制御点ごとの区間種別（いまの区間の表示に使う） */
  readonly segmentKinds?: readonly SegmentType[] | undefined;
  /** 直近の描画 FPS（デバッグ表示用） */
  readonly fps: number | undefined;
}) {
  const t = messages.hud;
  const [detail, setDetail] = useState(false);
  const [debug, setDebug] = useState(false);
  const snapshot = useSyncExternalStore(
    controller?.subscribe ?? noopSubscribe,
    controller?.getSnapshot ?? emptySnapshot,
  );
  const {
    attach: hudAttach,
    style: hudStyle,
    handle: hudHandle,
  } = useMovableWindow<HTMLElement>('hud', messages.windows.names.hud);
  const {
    attach: sectionAttach,
    style: sectionStyle,
    handle: sectionHandle,
  } = useMovableWindow<HTMLDivElement>('section', messages.windows.names.section);
  if (!ride || !controller) return null;

  const frame = frameAtTime(ride.result, snapshot.time);
  const v = hudValues(frame);
  const segment =
    ride.track.segment[Math.min(ride.track.count - 1, Math.round(v.distance / ride.track.spacing))];

  const kind = segment === undefined ? undefined : segmentKinds?.[segment];
  const section = kind === undefined ? undefined : SEGMENT_STYLES[kind];

  return (
    <>
      {kind !== undefined && section !== undefined && (
        <div
          ref={sectionAttach}
          className="ride-section"
          role="status"
          aria-label={messages.rideSection.label}
          data-testid="ride-section"
          data-kind={kind}
          style={{ borderColor: section.css, ...sectionStyle }}
        >
          {sectionHandle}
          <span
            className="ride-section__swatch"
            style={{ background: section.css }}
            aria-hidden="true"
          />
          <span aria-hidden="true">{section.icon}</span>{' '}
          {messages.rideSection[kind as 'station' | 'lift' | 'launch' | 'brake']}
        </div>
      )}
      <aside
        ref={hudAttach}
        className="hud"
        style={hudStyle}
        aria-label={t.label}
        data-testid="hud"
      >
        <dl className="hud__list">
          <Row label={t.time} value={`${v.time.toFixed(1)} s`} testId="hud-time" />
          <Row label={t.distance} value={`${v.distance.toFixed(0)} m`} testId="hud-distance" />
          <Row label={t.speed} value={`${v.speedKmh.toFixed(0)} km/h`} testId="hud-speed" />
          <Row label={t.altitude} value={`${v.altitude.toFixed(1)} m`} testId="hud-altitude" />
          <Row
            label={t.verticalG}
            value={`${formatSigned(v.verticalG, 2)} G`}
            testId="hud-vertical-g"
          />
          {detail && (
            <>
              <Row
                label={t.maxSpeed}
                value={`${(ride.stats.maxSpeed * 3.6).toFixed(0)} km/h`}
                testId="hud-max-speed"
              />
              <Row label={t.grade} value={formatGrade(v.gradePercent)} testId="hud-grade" />
              <Row label={t.bank} value={`${formatSigned(v.bankDegrees, 0)} °`} testId="hud-bank" />
              <Row
                label={t.longitudinalG}
                value={`${formatSigned(v.longitudinalG, 2)} G`}
                testId="hud-longitudinal-g"
              />
              <Row
                label={t.lateralG}
                value={`${formatSigned(v.lateralG, 2)} G`}
                testId="hud-lateral-g"
              />
              <Row
                label={t.combinedG}
                value={`${v.combinedG.toFixed(2)} G`}
                testId="hud-combined-g"
              />
              <Row
                label={t.segment}
                value={segment === undefined ? '—' : `${t.segmentNumber} ${String(segment + 1)}`}
                testId="hud-segment"
              />
            </>
          )}
          {debug && (
            <>
              <Row
                label={t.fps}
                value={fps === undefined ? '—' : `${String(fps)} FPS`}
                testId="hud-fps"
              />
              <Row
                label={t.computeTime}
                value={`${ride.computeMs.toFixed(0)} ms`}
                testId="hud-compute"
              />
              <Row
                label={t.engine}
                value={ride.result.meta.physicsEngineVersion}
                testId="hud-engine"
              />
            </>
          )}
        </dl>
        <div className="hud__toggles">
          {hudHandle}
          <ToolButton
            label={t.detail}
            pressed={detail}
            onClick={() => {
              setDetail((value) => !value);
            }}
            data-testid="hud-detail-toggle"
          />
          <ToolButton
            label={t.debug}
            pressed={debug}
            onClick={() => {
              setDebug((value) => !value);
            }}
            data-testid="hud-debug-toggle"
          />
        </div>
      </aside>
    </>
  );
}

function Row({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="hud__row">
      <dt>{label}</dt>
      <dd data-testid={testId}>{value}</dd>
    </div>
  );
}

function noopSubscribe(): () => void {
  return () => undefined;
}

const emptySnapshot = (() => {
  const snapshot = { time: 0, duration: 0, playing: false, rate: 1 as const, ended: false };
  return () => snapshot;
})();
